import {
  AssetRequest,
  AssetRequestCreateInput,
  AssetRequestPurchaseUnit,
  AssetRequestPurchaseUnitInput,
  AssetRequestType,
  Replenishment,
  ReplenishmentCreateInput,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import {
  ADOT_QUANTITY_DECIMALS,
  roundADOTQuantity,
} from './adotQuantityUtilities';

export const formatADOTConversion = ({
  unit,
  conversionNumber,
  baseUnit,
}: AssetRequestPurchaseUnit): string =>
  `1 ${unit} = ${conversionNumber} ${baseUnit}`;

export const getADOTPurchaseUnit = (
  purchaseUnit: AssetRequestPurchaseUnitInput,
  baseUnit: string,
): AssetRequestPurchaseUnit => ({
  unit: purchaseUnit?.unit || baseUnit || '',
  conversionNumber: purchaseUnit?.conversionNumber || 1,
  baseUnit: baseUnit || '',
});

export const isADOTBaseUnit = (purchaseUnit: AssetRequestPurchaseUnit) =>
  purchaseUnit.conversionNumber === 1 &&
  purchaseUnit.unit === purchaseUnit.baseUnit;

export const multiplyADOTConversion = (
  value: number,
  purchaseUnit: AssetRequestPurchaseUnit,
): number =>
  isNilOrEmpty(value)
    ? null
    : roundADOTQuantity(value * purchaseUnit.conversionNumber);

export const divideADOTConversion = (
  value: number,
  purchaseUnit: AssetRequestPurchaseUnit,
): number =>
  isNilOrEmpty(value)
    ? null
    : roundADOTQuantity(value / purchaseUnit.conversionNumber);

export const toADOTBaseReplenishment = <
  T extends Partial<ReplenishmentCreateInput>,
>(
  inputs: T,
  purchaseUnit: AssetRequestPurchaseUnit,
): T => ({
  ...inputs,
  quantityReceived: multiplyADOTConversion(
    inputs?.quantityReceived,
    purchaseUnit,
  ),
  unitCost: divideADOTConversion(inputs?.unitCost, purchaseUnit),
});

export const fromADOTBaseReplenishment = (
  replenishment: Replenishment,
  purchaseUnit: AssetRequestPurchaseUnit,
): Partial<ReplenishmentCreateInput> => ({
  invoiceNumber: replenishment?.invoiceNumber,
  dateReceived: replenishment?.dateReceived,
  quantityReceived: divideADOTConversion(
    replenishment?.quantityReceived,
    purchaseUnit,
  ),
  unitCost: multiplyADOTConversion(replenishment?.unitCost, purchaseUnit),
  totalCost: replenishment?.totalCost,
  comments: replenishment?.comments,
});

export const toADOTBaseCreateInputs = (
  input: AssetRequestCreateInput,
): AssetRequestCreateInput => {
  if (input?.type !== AssetRequestType.ADOT_REPLENISHMENT) {
    return input;
  }

  const { purchaseUnit, ...rest } = input;
  const conversion = getADOTPurchaseUnit(purchaseUnit, '');

  return {
    ...rest,
    ...(purchaseUnit && { purchaseUnit }),
    quantityOrdered: multiplyADOTConversion(input.quantityOrdered, conversion),
    replenishment: toADOTBaseReplenishment(input.replenishment, conversion),
  };
};

type ADOTReplenishmentTotals = {
  purchaseUnit: AssetRequestPurchaseUnit;
  orderedBase: number;
  receivedBase: number;
  remainingBase: number;
};

export const getADOTReplenishmentTotals = (
  request: AssetRequest,
): ADOTReplenishmentTotals => {
  const orderedBase = request?.quantityOrdered ?? 0;
  const receivedBase = roundADOTQuantity(
    (request?.replenishments ?? []).reduce(
      (acc, replenishment) => acc + (replenishment.quantityReceived ?? 0),
      0,
    ),
  );

  return {
    purchaseUnit: getADOTPurchaseUnit(
      request?.purchaseUnit,
      request?.purchaseUnit?.baseUnit || request?.assetUnit,
    ),
    orderedBase,
    receivedBase,
    remainingBase: roundADOTQuantity(orderedBase - receivedBase),
  };
};

const formatADOTQuantity = (value: number): string =>
  (value ?? 0).toLocaleString('en-US', {
    maximumFractionDigits: ADOT_QUANTITY_DECIMALS,
  });

export const formatADOTQuantityWithUnit = (
  value: number,
  unit: string,
): string =>
  unit ? `${formatADOTQuantity(value)} ${unit}` : formatADOTQuantity(value);

export const formatADOTUnitCost = (value: number, unit: string): string => {
  const cost = (value ?? 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: ADOT_QUANTITY_DECIMALS,
  });

  return unit ? `${cost} / ${unit}` : cost;
};

export const getADOTReplenishmentInputsDisabled = (
  inputs: Partial<ReplenishmentCreateInput>,
  allowZeroQuantity = false,
): boolean =>
  isNilOrEmpty(inputs?.invoiceNumber) ||
  isNilOrEmpty(inputs?.dateReceived) ||
  isNilOrEmpty(inputs?.quantityReceived) ||
  (!allowZeroQuantity && inputs?.quantityReceived === 0) ||
  isNilOrEmpty(inputs?.unitCost) ||
  isNilOrEmpty(inputs?.totalCost);
